var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from "axios";
import { hasValue, trimSlash } from "../../utils";
// Prebuilt, lowercased lookup for the active language. `$L` resolves keys through
// this flat map instead of rebuilding the embedded map + running Object.keys scans
// over the (688-key embedded + ~1.6MB DB) payloads on every call. Built once, held
// in memory for the session; a language switch reloads the page, and new
// registrations invalidate it so it rebuilds once on the next lookup.
let lookupIndex = null;
let indexLanguage = null;
export function registerLocalization(messages) {
    let Messages = window.UXPMessages;
    if (!Messages) {
        Messages = {};
    }
    // remove duplicates and merge the rest
    for (const code of Object.keys(messages)) {
        if (!Messages[code]) {
            Messages[code] = messages[code];
        }
    }
    window.UXPMessages = Messages;
    // Invalidate so the next lookup picks up the newly registered messages.
    lookupIndex = null;
}
/**
 * Builds a flat lowercased `code -> message` map for the given language.
 * Embedded (widget) messages first, then DB/custom messages overlaid so they win
 * (only when they have a value) — matching the previous lookup precedence.
 */
function buildLookupIndex(language) {
    var _a;
    const index = {};
    const embedded = window.UXPMessages || {};
    for (const code of Object.keys(embedded)) {
        index[code.toLowerCase()] = embedded[code][language];
    }
    const custom = ((_a = window.ServiceDesk) === null || _a === void 0 ? void 0 : _a.__AppMessages__) || {};
    for (const code of Object.keys(custom)) {
        if (hasValue(custom[code]))
            index[code.toLowerCase()] = custom[code];
    }
    return index;
}
function getLookupIndex(language) {
    if (!lookupIndex || indexLanguage !== language) {
        lookupIndex = buildLookupIndex(language);
        indexLanguage = language;
    }
    return lookupIndex;
}
/**
 * Returns registered UXP messages for the selected language.
 * @export
 */
export function getRegisterMessages(language) {
    const allMessages = window.UXPMessages || {};
    const messages = {};
    for (const code of Object.keys(allMessages)) {
        messages[code.toLowerCase()] = allMessages[code][language];
    }
    return messages;
}
export function getRegisterMessagesByCode(code) {
    const allMessages = window.UXPMessages || {};
    return allMessages[code] || allMessages[code.toLowerCase()] || {};
}
// this will return messages for selected language
export function getCustomMessages() {
    var _a;
    return (((_a = window.ServiceDesk) === null || _a === void 0 ? void 0 : _a.__AppMessages__) || {});
}
export function localize(language, code, params) {
    if (!hasValue(code))
        return code;
    // O(1) lookup against the prebuilt in-memory index (embedded + DB messages).
    let localizedMessage = getLookupIndex(language)[code.toLowerCase()];
    if (!hasValue(localizedMessage))
        return code;
    // replace params
    if (params && Object.keys(params).length > 0) {
        for (const key of Object.keys(params)) {
            localizedMessage = localizedMessage.replace(`$${key}`, params[key]);
        }
    }
    return localizedMessage;
}
export function getEnabledLanguages(languages) {
    if (!languages || (languages === null || languages === void 0 ? void 0 : languages.length) == 0)
        return [];
    return (languages || []).map(l => { var _a; return (Object.assign(Object.assign({}, l), { Direction: ((_a = l === null || l === void 0 ? void 0 : l.Code) === null || _a === void 0 ? void 0 : _a.startsWith('ar')) ? 'rtl' : 'ltr' })); });
}
export function onChangeLanguage(uxpContext, language) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const url = trimSlash(uxpContext.lucyUrl) + `/User/updatelanguage?language=${language}&userkey=${uxpContext.userKey}`;
            const res = yield axios.get(url);
            window.location.reload();
        }
        catch (e) {
            console.log('Unable to update the language. Error: ', e);
        }
    });
}
export function updateDirectionBasedOnLanguage(language, languages) {
    const lang = languages.find(l => l.Code == language);
    if (lang && lang.Direction == 'rtl') {
        document.documentElement.setAttribute('dir', 'rtl');
        return 'rtl';
    }
    else {
        document.documentElement.removeAttribute('dir');
        return 'ltr';
    }
}
